import React from 'react';

import { StatisticsView, TrailingReturn } from '@ndvr/reporting-api';

import { Card } from '@/components/ui/card';
import { cn } from '@/utils/cn';

import { HEAD_CLASS, TABLE_CARD_HEADER_CLASS, TABLE_CLASS, TABLE_FOOTER_CLASS } from '../../reporting/table-styles';
import { NAV_CHART_LINE_COLOR } from '../constants';
import { comparableBenchmarkReturn, statPercent, trailingPeriodLabel } from '../statistics-format';

interface TrailingReturnsCardProps {
    // The primary benchmark's statistics: its windows set the columns and its account returns the
    // account row.
    view: StatisticsView;
    // Every header benchmark whose statistics have loaded, with its chart color: a row each.
    comparisons: { name: string; color: string; view: StatisticsView }[];
    accountLabel: string;
}

interface SeriesRow {
    label: string;
    color: string;
    value: (trailingReturn: TrailingReturn) => string | null | undefined;
    emphasized: boolean;
}

// Trailing returns of the account and each header benchmark per trailing window (QTD through
// inception) — the "Trailing returns" card of the Advanced Analytics design, which adds a row per
// comparison. Windows the server omitted for lack of history simply have no column; a benchmark
// whose data does not cover the household's exact window for a column shows an em dash there.
export const TrailingReturnsCard = ({ view, comparisons, accountLabel }: TrailingReturnsCardProps) => {
    const rows: SeriesRow[] = [
        {
            label: accountLabel,
            color: NAV_CHART_LINE_COLOR,
            value: (trailingReturn) => trailingReturn.accountReturnPercent,
            emphasized: true,
        },
        ...comparisons.map((comparison) => ({
            label: comparison.name,
            color: comparison.color,
            value: (trailingReturn: TrailingReturn) => comparableBenchmarkReturn(comparison.view, trailingReturn),
            emphasized: false,
        })),
    ];
    // Whether any benchmark cell shows a dash for a mismatched window, which the footnote then explains.
    const hasUncoveredWindows = comparisons.some(({ view: benchmarkView }) =>
        view.trailingReturns.some((window) => comparableBenchmarkReturn(benchmarkView, window) === null),
    );

    return (
        <Card className="border-border overflow-hidden rounded-2xl border bg-white">
            <div className={cn(TABLE_CARD_HEADER_CLASS, 'border-b')}>
                <h3 className="font-heading text-cocoa-1000 text-xl">Trailing returns</h3>
                <span className="text-cocoa-400 text-xs">Account vs. benchmarks</span>
            </div>
            <table className={cn('w-full', TABLE_CLASS)}>
                <thead>
                    <tr className="border-b">
                        <th className="w-[22%] px-4 py-2.5" />
                        {view.trailingReturns.map((trailingReturn) => (
                            <th key={trailingReturn.period} className={`${HEAD_CLASS} px-4 py-2.5 text-right`}>
                                {trailingPeriodLabel(trailingReturn.period)}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr key={row.label} className="border-b last:border-b-0">
                            <td className="px-4 py-3 text-sm">
                                <span className="flex items-center gap-2">
                                    <span
                                        className="h-2.5 w-2.5 shrink-0 rounded-xs"
                                        style={{ backgroundColor: row.color }}
                                    />
                                    <span
                                        className={row.emphasized ? 'text-cocoa-900 font-semibold' : 'text-cocoa-500'}
                                    >
                                        {row.label}
                                    </span>
                                </span>
                            </td>
                            {view.trailingReturns.map((trailingReturn) => (
                                <td
                                    key={trailingReturn.period}
                                    className={`px-4 py-3 text-right text-sm tabular-nums ${
                                        row.emphasized ? 'text-cocoa-900 font-semibold' : 'text-cocoa-400'
                                    }`}
                                >
                                    {statPercent(row.value(trailingReturn))}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
            <div className={TABLE_FOOTER_CLASS}>
                Returns for periods exceeding 12 months are annualized.
                {hasUncoveredWindows
                    ? ' A dash marks a benchmark whose data does not cover the same months as the household.'
                    : null}
            </div>
        </Card>
    );
};
